import { Guid } from 'guid-typescript';

import { cvaPropertyUpdate } from '@features/opportunities/services/propertyUpdate';
import { PRODUCT_FEATURES } from '@shared/constants/product-features.constants';
import { FrequencyType } from '@shared/models/frequency';
import { isReverseMortgage } from '@shared/utilities/product/product-feature.util';

import {
  createOpportunityPropertyDefinition,
  OpportunityPropertyDefinition
} from '../opportunity-property-definition';

export enum ProductSelectionType {
  Single = 'Single Product',
  Package = 'Multi Products'
}
export type ProductSelectionTypes = `${ProductSelectionType}`;

export enum Recommendation {
  Adviser = 'Adviser',
  Customer = 'Customer'
}
export type RecommendationType = `${Recommendation}`;

export enum RateType {
  Variable = 'Variable',
  Fixed = 'Fixed'
}
export type RateTypes = `${RateType}`;

export enum RepaymentTypes {
  PI = 'Principal & Interest',
  IO = 'Interest Only'
}
export type RepaymentType = `${RepaymentTypes}`;

export function repaymentTypeToEnum(
  repaymentType: RepaymentType
): RepaymentTypes {
  switch (repaymentType) {
    case 'Interest Only':
      return RepaymentTypes.IO;
    case 'Principal & Interest':
      return RepaymentTypes.PI;
  }
}

export enum RiskGrade {
  AAA = 'AAA',
  AA = 'AA',
  A = 'A',
  Aminus = 'Aminus',
  Bplus = 'Bplus',
  B = 'B',
  Bminus = 'Bminus',
  Rejected = 'Rejected',
  C = 'C',
  NotApplicable = 'NotApplicable'
}

export interface ProductSearchCriteria {
  features: Array<ProductFeature>;
  repaymentType: RepaymentType;
  rateType: RateType;
  rateTypes: RateType[];
}

export type PackageOption = {
  productId: string;
  productRateId: string;
  loanAmount: number;
  proPack: boolean;
  splitAccount: boolean;
  initialRepaymentType: string;
  capitalizeInterestOnlyPayments?: boolean;
};

export type SelectedPackage = {
  productSelectionId: string;
  products: Array<PackageOption>;
};

export type ProductSelectionPayload = {
  selectionType: ProductSelectionType;
  selections: Array<SelectedOption> | SelectedPackage;
};

export type SelectedOption = {
  productSelectionId: string;
  productId: string;
  productRateId: string;
  amount: number | null;
  proPack: boolean;
  splitAccount: boolean;
  initialRepaymentType: string;
  capitalizeInterestOnlyPayments?: boolean;
};

export type ProductSelectionInputs = {
  governmentDutyAmount: number | null;
  lvr: string | null;
  totalPurposeAmount: number | null;
  bridgingPurchaseAmount?: number | null;
  bridgingPurchaseLoanAmount?: number | null;
  bridgingEndDebt?: number | null;
  bridgingEndDebtPerSelection?: BridgingEndDebtPerSelection[];
};

export type BridgingEndDebtPerSelection = {
  endDebt: number;
  productSelectionId: string;
};

export type RateRange = {
  baseRate: OpportunityPropertyDefinition<number>;
  maxRate: OpportunityPropertyDefinition<number>;
};

export interface EditTerm {
  rate: OpportunityPropertyDefinition<number>;
  rateRange: RateRange | null;
  discountId: OpportunityPropertyDefinition<string>;
  term: OpportunityPropertyDefinition<number>;
  rateType: OpportunityPropertyDefinition<RateTypes>;
  repaymentType: OpportunityPropertyDefinition<RepaymentType>;
  repayments: number;
}

export interface EditMultiTerm {
  initialTerm: EditTerm;
  revertTerm: EditTerm;
}

export interface EditProductStructure {
  singleTerm: EditTerm;
  multiTerm: EditMultiTerm;
}

export interface EditProductFee {
  id: number;
  type: OpportunityPropertyDefinition<string>;
  category: OpportunityPropertyDefinition<FeeCategory>;
  amount: OpportunityPropertyDefinition<number>;
  percentageOfLoanAmount: OpportunityPropertyDefinition<number>;
  waivedInProPack: OpportunityPropertyDefinition<boolean>;
  waivedInSplit: OpportunityPropertyDefinition<boolean>;
}

export interface EditProductTerm {
  min: OpportunityPropertyDefinition<number>;
  max: OpportunityPropertyDefinition<number>;
  maxInterestOnlyTerm: OpportunityPropertyDefinition<number>;
}

export interface EditLmi {
  amount: OpportunityPropertyDefinition<number>;
  capitalised: OpportunityPropertyDefinition<number>;
  overrideAmount: OpportunityPropertyDefinition<number>;
}

export interface EditProduct {
  productId: OpportunityPropertyDefinition<number>;
  productRateId: OpportunityPropertyDefinition<string>;
  providerId: OpportunityPropertyDefinition<string>;
  provider: OpportunityPropertyDefinition<string>;
  name: OpportunityPropertyDefinition<string>;
  fees: EditProductFee[];
  lmi: EditLmi;
  includedGovDuties: OpportunityPropertyDefinition<number>;
  includedLenderFees: OpportunityPropertyDefinition<number>;
  balloonAmount: OpportunityPropertyDefinition<number>;
  maxLvr: OpportunityPropertyDefinition<number>;
  productTerm: EditProductTerm;
  structure: EditProductStructure;
  features: OpportunityPropertyDefinition<string[]>;
  loanTerm: number;
  repaymentFrequency: OpportunityPropertyDefinition<FrequencyType>;
  proPack: OpportunityPropertyDefinition<boolean>;
  libertyProductGroupId: OpportunityPropertyDefinition<number>;
  minMonthsBeforeInterestLoading?: OpportunityPropertyDefinition<number>;
  interestLoading?: OpportunityPropertyDefinition<number>;
  capitalizeInterestOnlyPayments?: OpportunityPropertyDefinition<boolean>;
  isBridgingProduct?: OpportunityPropertyDefinition<boolean>;
}

export interface EditPackageProduct {
  id: string;
  loanAmount: OpportunityPropertyDefinition<number>;
  product: EditProduct;
  proPack: boolean;
  splitAccount: boolean;
}

export interface EditPackage {
  name: OpportunityPropertyDefinition<string>;
  products: Array<EditPackageProduct>;
}

export interface Repayments {
  initialRepayments: number;
  revertRepayments: number;
}

export interface RepaymentSchedule {
  month: number;
  repaymentAmount: number;
}

export interface SearchFilters {
  rateType: RateType;
  repaymentType: RepaymentType;
  features: Array<ProductFeature>;
  rateTypes: RateType[];
  repaymentTypes: RepaymentType[];
}

export interface ProductSearchResultStructure {
  id: string;
  discountId: string;
  rate: number;
  term: number;
  rateType: RateTypes;
}

export interface ProductSearchResultDetails {
  name: string;
  maxLVR: number;
  riskGrade: RiskGrade;
  structure: ProductSearchResultStructure[];
  fees: ProductFee[];
  minTerm: number;
  maxTerm: number;
  maxInterestOnlyTerm: number;
  features: string[];
  maxLoanAmount: number;
  minMonthsBeforeInterestLoading?: number;
  interestLoading?: number;
}

export interface ProductSearchResult {
  id: string;
  productRateId: string;
  providerId: string;
  provider: string;
  loyaltyScore: number;
  minTurnaroundTime: number;
  maxTurnaroundTime: number;
  product: ProductSearchResultDetails;
  opportunityLvr: number;
  amount: number;
  libertyProductGroupId: number | null;
}

export interface ServicingOverride {
  referenceNumber: string;
  type: string;
  reason: string;
  createdBy: string;
  createdAt: string;
}

export interface ProductSelection {
  id: string;
  provider: string;
  recommendation: RecommendationType | null;
  packageProduct: Package | null;
  singleProduct: Product | null;
  servicingOverride: ServicingOverride | null;
}

export interface ProductSelectionResponse {
  selections: ProductSelection[];
  otherProductsUnsuitable: boolean;
}

export interface PackageProduct {
  loanAmount: number;
  product: Product;
}

export interface Package {
  id: string;
  name: string;
  products: Array<PackageProduct>;
}

export interface ProductRate {
  id: string;
  initialDiscountId: string;
  revertDiscountId: string;
  interestRate: number;
  fixedRate: boolean;
  initialTerm: number;
  minLvr: number;
  maxLvr: number;
  revertRate: number;
  interestOnlyAddOn: number;
}

export interface Product {
  productId: number;
  productRateId: string;
  providerId: string;
  provider: string;
  name: string;
  setupFees: number;
  monthlyFees: number;
  annualFees: number;
  fees: Array<ProductFee>;
  includedGovDuties: number | null;
  includedLenderFees: number | null;
  isProductValid: boolean;
  isProductRateValid: boolean;
  isRateApplicable: boolean;
  maxLvr: number;
  productTerm: ProductTerm;
  structure: ProductStructure;
  features: Array<string>;
  loanAmount: number;
  loanTerm: number;
  repaymentFrequency: FrequencyType;
  totalRepayments: number;
  proPack: boolean;
  minMonthsBeforeInterestLoading?: number | null;
  interestLoading?: number | null;
  capitalizeInterestOnlyPayments?: boolean | null;
  isBridgingProduct?: boolean | null;
}

type FeeCategory =
  | 'Setup'
  | 'Exit'
  | 'Monthly'
  | 'Annual'
  | 'Split'
  | 'ProPack'
  | 'Funding';

export interface ProductFee {
  id: number;
  type: string;
  category: FeeCategory;
  amount: number;
  percentageOfLoanAmount: number;
  waivedInProPack: boolean;
  waivedInSplit: boolean;
}

export interface ProductTerm {
  min: number;
  max: number;
  maxInterestOnlyTerm: number;
}

export interface Term {
  rate: number;
  discountId: string;
  term: number;
  rateType: RateTypes;
  repaymentType: RepaymentType;
  repayments: number;
}

export interface MultiTerm {
  initialTerm: Term;
  revertTerm: Term;
}

export interface ProductStructure {
  singleTerm: Term | null;
  multiTerm: MultiTerm | null;
}

export interface ProductFeature {
  id: string;
  name: string;
}

export interface InclusionDetail {
  include: boolean;
  amount: number | null;
}

export interface LvrAndMaxCap {
  maxCap: number;
  lvr: number;
  lmi: number;
}

export function isPackage(object: ProductSelection) {
  return object.packageProduct != null;
}

export function calculateTerm(term: number): number {
  return term / 12;
}

export function getProducts(object: ProductSelection): Product[] {
  if (isPackage(object)) {
    return (
      object.packageProduct?.products.map(
        (packageProduct) => packageProduct.product
      ) || []
    );
  } else if (object.singleProduct) {
    return [object.singleProduct];
  }
  return [];
}

export function createProductPayload(product: EditProduct, isPackage: boolean) {
  const getStructure = () => {
    if (hasSingleTerm(product.structure)) {
      return {
        singleTerm: {
          rate: cvaPropertyUpdate(product.structure.singleTerm.rate, (x) => +x),
          discountId: cvaPropertyUpdate(
            product.structure.singleTerm.discountId,
            (x) => x
          ),
          term: cvaPropertyUpdate(product.structure.singleTerm.term, (x) => +x)
        },
        multiTerm: null
      };
    } else {
      let terms = product.structure.multiTerm;
      return {
        singleTerm: null,
        multiTerm: {
          initialTerm: {
            rate: cvaPropertyUpdate(terms.initialTerm.rate, (x) => +x),
            discountId: cvaPropertyUpdate(
              product.structure.multiTerm.initialTerm.discountId,
              (x) => x
            ),
            term: cvaPropertyUpdate(terms.initialTerm.term, (x) => +x)
          },
          revertTerm: {
            rate: cvaPropertyUpdate(terms.revertTerm.rate, (x) => +x),
            discountId: cvaPropertyUpdate(
              product.structure.multiTerm.revertTerm.discountId,
              (x) => x
            ),
            term: cvaPropertyUpdate(terms.revertTerm.term, (x) => +x)
          }
        }
      };
    }
  };

  const getFees = () => {
    const getFee = (fee: EditProductFee) => {
      let amount = cvaPropertyUpdate(fee.amount, (x) => +x);
      return {
        amount: amount
      };
    };

    let fees: any = {};
    product.fees.forEach((fee) => (fees[fee.id] = getFee(fee)));
    return fees;
  };

  const getLmi = () => {
    if (product.lmi) {
      let lmi: any = {
        amount: cvaPropertyUpdate(product.lmi.amount, (x) => +x),
        capitalised: product.lmi.capitalised
          ? cvaPropertyUpdate(product.lmi.capitalised, (x) => +x)
          : null,
        overrideAmount: product.lmi.overrideAmount
          ? cvaPropertyUpdate(product.lmi.overrideAmount, (x) => +x)
          : null
      };
      return lmi;
    }
  };

  console.log(
    'capitalizeInterestOnlyPayments',
    product.capitalizeInterestOnlyPayments
  );
  return {
    productRateId: cvaPropertyUpdate(product.productRateId, (x) => x),
    fees: getFees(),
    lmi: getLmi(),
    includedGovDuties: product.includedGovDuties
      ? cvaPropertyUpdate(product.includedGovDuties, (x) => +x)
      : null,
    includedLenderFees: product.includedLenderFees
      ? cvaPropertyUpdate(product.includedLenderFees, (x) => +x)
      : null,
    balloonAmount: product.balloonAmount
      ? cvaPropertyUpdate(product.balloonAmount, (x) => +x)
      : null,
    repaymentFrequency: cvaPropertyUpdate(product.repaymentFrequency, (x) => x),
    structure: getStructure(),
    capitalizeInterestOnlyPayments: product.capitalizeInterestOnlyPayments
      ? cvaPropertyUpdate(product.capitalizeInterestOnlyPayments, (x) => x)
      : null
  };
}

export function structureToTerms(structure: ProductStructure): Term[] {
  if (structure.singleTerm != null) {
    return [structure.singleTerm];
  } else if (structure.multiTerm != null) {
    return [structure.multiTerm.initialTerm, structure.multiTerm.revertTerm];
  } else {
    return [];
  }
}

export function convertSearchResultStructureToProductStructure(
  optionStructures: ProductSearchResultStructure[],
  repaymentType: RepaymentType
): ProductStructure {
  let initialTerm: Term = {
    rate: optionStructures[0].rate,
    discountId: optionStructures[0].discountId,
    term: optionStructures[0].term,
    rateType: optionStructures[0].rateType,
    repaymentType: repaymentType,
    repayments: 0
  };
  if (optionStructures.length == 2) {
    let revertTerm = {
      rate: optionStructures[1].rate,
      discountId: optionStructures[1].discountId,
      term: optionStructures[1].term,
      rateType: optionStructures[1].rateType,
      repaymentType: RepaymentTypes.PI,
      repayments: 0
    };
    let multiTerm = {
      initialTerm: initialTerm,
      revertTerm: revertTerm
    };
    return {
      singleTerm: null,
      multiTerm: multiTerm
    };
  } else {
    return {
      singleTerm: initialTerm,
      multiTerm: null
    };
  }
}

export function convertEditStructureToProductStructure(
  structure: EditProductStructure
): ProductStructure {
  if (hasSingleTerm(structure)) {
    return {
      singleTerm: {
        rate: structure.singleTerm.rate.value!,
        discountId: structure.singleTerm.discountId.value!,
        rateType: structure.singleTerm.rateType.value!,
        repaymentType: structure.singleTerm.repaymentType.value!,
        repayments: structure.singleTerm.repayments,
        term: structure.singleTerm.term.value!
      },
      multiTerm: null
    };
  } else {
    return {
      singleTerm: null,
      multiTerm: {
        initialTerm: {
          rate: structure.multiTerm.initialTerm.rate.value!,
          discountId: structure.multiTerm.initialTerm.discountId.value!,
          rateType: structure.multiTerm.initialTerm.rateType.value!,
          repaymentType: structure.multiTerm.initialTerm.repaymentType.value!,
          repayments: structure.multiTerm.initialTerm.repayments,
          term: structure.multiTerm.initialTerm.term.value!
        },
        revertTerm: {
          rate: structure.multiTerm.revertTerm.rate.value!,
          discountId: structure.multiTerm.revertTerm.discountId.value!,
          rateType: structure.multiTerm.revertTerm.rateType.value!,
          repaymentType: structure.multiTerm.revertTerm.repaymentType.value!,
          repayments: structure.multiTerm.revertTerm.repayments,
          term: structure.multiTerm.revertTerm.term.value!
        }
      }
    };
  }
}

export function convertEditFeeToFeeModel(editFee: EditProductFee): ProductFee {
  return {
    id: editFee.id!,
    type: editFee.type.value!,
    category: editFee.category.value!,
    amount: editFee.amount.value!,
    percentageOfLoanAmount: editFee.percentageOfLoanAmount.value!,
    waivedInProPack: editFee.waivedInProPack.value!,
    waivedInSplit: editFee.waivedInSplit.value!
  };
}

export function convertEditProductTermToProductTermModel(
  editTerm: EditProductTerm
): ProductTerm {
  return {
    min: editTerm.min.value!,
    max: editTerm.max.value!,
    maxInterestOnlyTerm: editTerm.maxInterestOnlyTerm.value!
  };
}

export function convertEditProductToProductModel(
  editProduct: EditProduct,
  loanAmount: number
): Product {
  let fees = editProduct.fees.map((fee) => convertEditFeeToFeeModel(fee));
  let setupFees = fees
    .filter((fee) => fee.category == 'Setup' || fee.category == 'Split')
    .map((fee) => fee.amount);
  let monthlyFees = fees
    .filter((fee) => fee.category == 'Monthly')
    .map((fee) => fee.amount);
  let annualFees = fees
    .filter((fee) => fee.category == 'Annual' || fee.category == 'ProPack')
    .map((fee) => fee.amount);

  return {
    productId: editProduct.productId.value!,
    productRateId: editProduct.productRateId.value!,
    providerId: editProduct.providerId.value!,
    provider: editProduct.provider.value!,
    name: editProduct.name.value!,
    setupFees:
      setupFees.length > 0
        ? setupFees.reduce((previous, current) => previous + current, 0)
        : 0,
    monthlyFees:
      monthlyFees.length > 0
        ? monthlyFees.reduce((previous, current) => previous + current, 0)
        : 0,
    annualFees:
      annualFees.length > 0
        ? annualFees.reduce((previous, current) => previous + current, 0)
        : 0,
    fees: fees,
    includedGovDuties: editProduct.includedGovDuties.value ?? null,
    includedLenderFees: editProduct.includedLenderFees.value ?? null,
    isProductValid: true,
    isProductRateValid: true,
    isRateApplicable: true,
    maxLvr: editProduct.maxLvr.value!,
    productTerm: convertEditProductTermToProductTermModel(
      editProduct.productTerm
    ),
    structure: convertEditStructureToProductStructure(editProduct.structure),
    features: editProduct.features.value ?? [],
    loanAmount: loanAmount,
    loanTerm: editProduct.loanTerm,
    repaymentFrequency: editProduct.repaymentFrequency.value!,
    totalRepayments: 0,
    proPack: editProduct.proPack.value!,
    minMonthsBeforeInterestLoading:
      editProduct.minMonthsBeforeInterestLoading?.value ?? null,
    interestLoading: editProduct.interestLoading?.value ?? null,
    capitalizeInterestOnlyPayments: isReverseMortgage(editProduct.features)
      ? true // Force true for reverse mortgages
      : editProduct.capitalizeInterestOnlyPayments?.value ?? null,
    isBridgingProduct:
      editProduct.features.value?.includes(PRODUCT_FEATURES.BRIDGING) ?? false
  };
}

export function mapProductSearchResultToEditModel(
  productOption: ProductSearchResult,
  searchCriteria: ProductSearchCriteria,
  repaymentFrequency: FrequencyType
): EditPackageProduct {
  let loanTerm = productOption.product.structure
    .map((structure) => structure.term)
    .reduce((prev, curr) => prev + curr, 0);
  let proPack = searchCriteria.features
    .map((feature) => feature.name)
    .includes('Pro-Pack / Discount');
  let splitAccount = searchCriteria.features
    .map((feature) => feature.name)
    .includes('Split Loan');
  let fees = productOption.product.fees
    .filter(
      (fee) =>
        (splitAccount && (fee.category == 'Split' || !fee.waivedInSplit)) ||
        (!splitAccount && fee.category != 'Split')
    )
    .filter(
      (fee) =>
        (proPack && (fee.category == 'ProPack' || !fee.waivedInProPack)) ||
        (!proPack && fee.category != 'ProPack')
    );

  return {
    id: Guid.create().toString(),
    loanAmount: createOpportunityPropertyDefinition(0),
    proPack: proPack,
    splitAccount: splitAccount,
    product: {
      productId: createOpportunityPropertyDefinition(
        parseInt(productOption.id)
      ),
      productRateId: createOpportunityPropertyDefinition(
        productOption.productRateId
      ),
      providerId: createOpportunityPropertyDefinition(productOption.providerId),
      provider: createOpportunityPropertyDefinition(productOption.provider),
      name: createOpportunityPropertyDefinition(productOption.product.name),
      fees: fees.map((fee) => {
        return {
          id: fee.id,
          type: createOpportunityPropertyDefinition(fee.type),
          category: createOpportunityPropertyDefinition(fee.category),
          amount: createOpportunityPropertyDefinition(fee.amount),
          percentageOfLoanAmount: createOpportunityPropertyDefinition(
            fee.percentageOfLoanAmount
          ),
          waivedInProPack: createOpportunityPropertyDefinition(
            fee.waivedInProPack
          ),
          waivedInSplit: createOpportunityPropertyDefinition(fee.waivedInSplit)
        };
      }),
      lmi: {
        amount: createOpportunityPropertyDefinition(),
        capitalised: createOpportunityPropertyDefinition(),
        overrideAmount: createOpportunityPropertyDefinition()
      },
      includedGovDuties: createOpportunityPropertyDefinition(),
      includedLenderFees: createOpportunityPropertyDefinition(),
      balloonAmount: createOpportunityPropertyDefinition(),
      maxLvr: createOpportunityPropertyDefinition(productOption.product.maxLVR),
      productTerm: {
        min: createOpportunityPropertyDefinition(productOption.product.minTerm),
        max: createOpportunityPropertyDefinition(productOption.product.maxTerm),
        maxInterestOnlyTerm: createOpportunityPropertyDefinition(
          productOption.product.maxInterestOnlyTerm
        )
      },
      structure: {
        singleTerm: {
          rate:
            productOption.product.structure.length == 1
              ? createOpportunityPropertyDefinition(
                  productOption.product.structure[0].rate
                )
              : createOpportunityPropertyDefinition(),
          rateRange: null,
          discountId:
            productOption.product.structure.length == 1
              ? createOpportunityPropertyDefinition(
                  productOption.product.structure[0].discountId
                )
              : createOpportunityPropertyDefinition(),
          term:
            productOption.product.structure.length == 1
              ? createOpportunityPropertyDefinition(
                  productOption.product.structure[0].term
                )
              : createOpportunityPropertyDefinition(),
          rateType:
            productOption.product.structure.length == 1
              ? createOpportunityPropertyDefinition(
                  productOption.product.structure[0].rateType
                )
              : createOpportunityPropertyDefinition(),
          repaymentType:
            productOption.product.structure.length == 1
              ? createOpportunityPropertyDefinition(
                  searchCriteria.repaymentType
                )
              : createOpportunityPropertyDefinition(),
          repayments: 0
        },
        multiTerm: {
          initialTerm: {
            rate:
              productOption.product.structure.length == 2
                ? createOpportunityPropertyDefinition(
                    productOption.product.structure[0].rate
                  )
                : createOpportunityPropertyDefinition(),
            rateRange: null,
            discountId:
              productOption.product.structure.length == 1
                ? createOpportunityPropertyDefinition(
                    productOption.product.structure[0].discountId
                  )
                : createOpportunityPropertyDefinition(),
            term:
              productOption.product.structure.length == 2
                ? createOpportunityPropertyDefinition(
                    productOption.product.structure[0].term
                  )
                : createOpportunityPropertyDefinition(),
            rateType:
              productOption.product.structure.length == 2
                ? createOpportunityPropertyDefinition(
                    productOption.product.structure[0].rateType
                  )
                : createOpportunityPropertyDefinition(),
            repaymentType:
              productOption.product.structure.length == 2
                ? createOpportunityPropertyDefinition(
                    searchCriteria.repaymentType
                  )
                : createOpportunityPropertyDefinition(),
            repayments: 0
          },
          revertTerm: {
            rate:
              productOption.product.structure.length == 2
                ? createOpportunityPropertyDefinition(
                    productOption.product.structure[1].rate
                  )
                : createOpportunityPropertyDefinition(),
            rateRange: null,
            discountId:
              productOption.product.structure.length == 2
                ? createOpportunityPropertyDefinition(
                    productOption.product.structure[1].discountId
                  )
                : createOpportunityPropertyDefinition(),
            term:
              productOption.product.structure.length == 2
                ? createOpportunityPropertyDefinition(
                    productOption.product.structure[1].term
                  )
                : createOpportunityPropertyDefinition(),
            rateType:
              productOption.product.structure.length == 2
                ? createOpportunityPropertyDefinition(
                    productOption.product.structure[1].rateType
                  )
                : createOpportunityPropertyDefinition(),
            repaymentType:
              productOption.product.structure.length == 2
                ? createOpportunityPropertyDefinition(RepaymentTypes.PI)
                : createOpportunityPropertyDefinition(),
            repayments: 0
          }
        }
      },
      proPack: createOpportunityPropertyDefinition(
        searchCriteria.features
          .map((feature) => feature.name)
          .includes('Pro-Pack / Discount')
      ),
      features: createOpportunityPropertyDefinition(
        productOption.product.features
      ),
      loanTerm: loanTerm,
      repaymentFrequency:
        createOpportunityPropertyDefinition(repaymentFrequency),
      libertyProductGroupId: createOpportunityPropertyDefinition(),
      minMonthsBeforeInterestLoading: productOption.product
        .minMonthsBeforeInterestLoading
        ? createOpportunityPropertyDefinition(
            productOption.product.minMonthsBeforeInterestLoading
          )
        : undefined,
      interestLoading: productOption.product.interestLoading
        ? createOpportunityPropertyDefinition(
            productOption.product.interestLoading
          )
        : undefined,
      capitalizeInterestOnlyPayments:
        createOpportunityPropertyDefinition(false),
      isBridgingProduct: createOpportunityPropertyDefinition(
        productOption.product.features?.includes(PRODUCT_FEATURES.BRIDGING) ??
          false
      )
    }
  };
}

export function hasSingleTerm(structure: EditProductStructure): boolean {
  return structure.singleTerm.rate.value != undefined;
}

export function hasMultiTerm(structure: EditProductStructure): boolean {
  return structure.multiTerm.initialTerm.rate.value != undefined;
}

export function hasInterestOnlyTerm(structure: EditProductStructure): boolean {
  return (
    structure.multiTerm.initialTerm.repaymentType.value == RepaymentTypes.IO
  );
}

export function getLoanTerm(structure: EditProductStructure): number {
  return (
    structure.singleTerm.term.value ??
    (structure.multiTerm.initialTerm.term.value ?? 0) +
      (structure.multiTerm.revertTerm.term.value ?? 0)
  );
}

export function getProviderName(productSelection: ProductSelection): string {
  return isPackage(productSelection)
    ? productSelection.provider
    : productSelection.singleProduct?.provider ?? '';
}

export function hasInvalidProduct(productSelection: ProductSelection): boolean {
  if (isPackage(productSelection)) {
    return (
      getInvalidProductsInPackage(productSelection.packageProduct!).length > 0
    );
  } else {
    return !productSelection.singleProduct!.isProductValid;
  }
}

export function getInvalidProductsInPackage(selection: Package): number[] {
  return selection.products
    .filter((packageProduct) => !packageProduct.product.isProductValid)
    .map((packageProduct) => packageProduct.product.productId);
}
